import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiSave, HiX } from "react-icons/hi";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

interface FaqFormData {
  categoryId: number;
  question: string;
  answer: string;
  displayOrder: number;
  isActive: boolean;
}

interface FaqFormErrors {
  categoryId?: string;
  question?: string;
  answer?: string;
}

interface FaqCategoryOption {
  id: number;
  name: string;
}

export const CreateFaq: React.FC = () => {
  const { t, i18n } = useTranslation(["faq", "common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState<FaqFormData>({
    categoryId: 0,
    question: "",
    answer: "",
    displayOrder: 0,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FaqFormErrors>({});
  const [categories, setCategories] = useState<FaqCategoryOption[]>([]);

  React.useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await adminApi.call("/faqcategory?page=1&pageSize=500", "GET");

        if (!response.success) {
          return;
        }

        setCategories(response.data?.items || []);
      } catch (error) {
        console.error("Error fetching FAQ categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FaqFormErrors = {};

    if (!formData.categoryId) {
      newErrors.categoryId = t("faq:validation.categoryRequired");
    }

    if (!formData.question.trim()) {
      newErrors.question = t("faq:validation.questionRequired");
    }

    if (!formData.answer.trim()) {
      newErrors.answer = t("faq:validation.answerRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await adminApi.call("/faq", "POST", formData);

      if (!response.success) {
        showErrorToast(response.error || t("faq:messages.createError"));
        return;
      }

      showSuccessToast(t("faq:messages.createSuccess"));
      setTimeout(() => {
        navigate("/faqs");
      }, 1500);
    } catch (error) {
      showErrorToast(t("faq:messages.createError"));
      console.error("Error creating FAQ:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t("faq:createFaq")}
      subtitle={t("faq:subtitle")}
      breadcrumbs={[
        { label: t("faq:breadcrumbs.faq"), path: "/faqs" },
        { label: t("faq:breadcrumbs.create") }
      ]}
      className={isRTL ? "rtl" : "ltr"}
      headerActions={
        <div className="header-actions">
          <ActionButton variant="secondary" icon={HiX} onClick={() => navigate("/faqs")}>
            {t("common:cancel")}
          </ActionButton>
          <ActionButton variant="primary" icon={HiSave} onClick={handleSubmit} loading={loading} disabled={loading}>
            {t("common:save")}
          </ActionButton>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label" htmlFor="categoryId">
              {t("faq:fields.category")} <span className="required">*</span>
            </label>
            <select
              id="categoryId"
              className={`form-select ${errors.categoryId ? "is-invalid" : ""}`}
              value={formData.categoryId}
              onChange={(e) => {
                setFormData({ ...formData, categoryId: Number(e.target.value) || 0 });
                if (errors.categoryId) setErrors((prev) => ({ ...prev, categoryId: undefined }));
              }}
              disabled={loading}
            >
              <option value={0}>{t("faq:placeholders.category")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {(() => {
                    try {
                      if (category.name && category.name.startsWith("{")) {
                        const parsed = JSON.parse(category.name);
                        return isRTL ? parsed.ar || parsed.en : parsed.en || parsed.ar;
                      }
                    } catch {
                      return category.name;
                    }

                    return category.name;
                  })()}
                </option>
              ))}
            </select>
            {errors.categoryId && <div className="invalid-feedback d-block">{errors.categoryId}</div>}
          </div>

          <div className="form-group full-width">
            <MultiLanguageTextInput
              label={t("faq:fields.question")}
              value={formData.question}
              onChange={(value) => {
                setFormData({ ...formData, question: value });
                if (errors.question) setErrors((prev) => ({ ...prev, question: undefined }));
              }}
              placeholder={t("faq:placeholders.question")}
              error={errors.question}
              disabled={loading}
              required
              isRTL={isRTL}
            />
          </div>

          <div className="form-group full-width">
            <MultiLanguageTextInput
              label={t("faq:fields.answer")}
              value={formData.answer}
              onChange={(value) => {
                setFormData({ ...formData, answer: value });
                if (errors.answer) setErrors((prev) => ({ ...prev, answer: undefined }));
              }}
              placeholder={t("faq:placeholders.answer")}
              error={errors.answer}
              disabled={loading}
              required
              isRTL={isRTL}
              multiline
              rows={5}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="displayOrder">{t("faq:fields.displayOrder")}</label>
            <input
              type="number"
              id="displayOrder"
              className="form-control"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) || 0 })}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t("faq:fields.isActive")}</label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                disabled={loading}
              />
              <label className="form-check-label">
                {formData.isActive ? t("faq:status.active") : t("faq:status.inactive")}
              </label>
            </div>
          </div>
        </div>
      </form>
    </PageLayout>
  );
};
